<template>
  <div class="updates-container">
    <!-- Header Section -->
    <header class="page-header">
      <div class="header-content">
        <span class="category-badge">Roadmap & Release Notes</span>
        <h1 style="color: #FFFFFF;">LSA Command Center</h1>
        <p class="subtitle">Track platform evolution and upcoming feature developments.</p>
      </div>
    </header>

    <div class="content-grid">
      <!-- Upcoming Tasks Sidebar / Section -->
      <section class="card tasks-card">
        <div class="card-header">
          <div class="title-with-icon">
            <span class="icon">🚀</span>
            <h2>Upcoming Features & Tasks</h2>
          </div>
          <span class="task-count">{{ upcomingTasks.length }} Items</span>
        </div>

        <ul class="task-list">
          <li v-for="(task, index) in upcomingTasks" :key="index" class="task-item">
            <label class="task-label">
              <span class="checkmark"></span>
              <span class="task-text">{{ task }}</span>
            </label>
          </li>
        </ul>
      </section>

      <!-- Timeline Version History Section -->
      <section class="card history-card">
        <div class="card-header">
          <div class="title-with-icon">
            <span class="icon">📜</span>
            <h2>Version History</h2>
          </div>
        </div>

        <div class="timeline">
          <div 
            v-for="(update, index) in versionHistory" 
            :key="update.date" 
            class="timeline-item"
          >
            <!-- Timeline Line & Node -->
            <div class="timeline-marker">
              <div class="timeline-node" :class="{ 'latest': index === 0 }"></div>
              <div v-if="index !== versionHistory.length - 1" class="timeline-line"></div>
            </div>

            <!-- Content Card -->
            <div class="timeline-content">
              <div class="timeline-header">
                <h3 class="update-title">{{ update.title }}</h3>
                <span class="update-date" :class="{ 'badge-latest': index === 0 }">
                  {{ index === 0 ? 'Latest • ' + update.date : update.date }}
                </span>
              </div>
              <p class="update-description">{{ update.description }}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LSACommandUpdates',
  data() {
    return {
      upcomingTasks: [
        "Implement Grok voice for our AI agents",
      ],
      versionHistory: [
        {
          title: "Platform Feature Strategies",
          date: "July 15, 2026",
          description: "Implemented automated referral commission deductions, researched speech technology, and optimized web chat to prioritize visitor qualification."
        },
        {
          title: "Communication Protocols & LSA Optimization",
          date: "July 10, 2026",
          description: "Standardized client meetings to a biweekly cadence and focused on quantifying service benefits through lead quality improvements."
        },
        {
          title: "Strategic Prioritization & Tagging",
          date: "July 1, 2026",
          description: "Established AI tagging for high-value projects and improved warm transfer protocols for lead conversion."
        },
        {
          title: "Process & Onboarding Adjustments",
          date: "June 29, 2026",
          description: "Optimized white-label onboarding processes and reported a 25% decrease in lead costs through hybrid AI/human models."
        },
        {
          title: "Security & Performance Metrics",
          date: "June 24, 2026",
          description: "Addressed automated messaging security concerns and developed monetization features for labor-hour savings."
        },
        {
          title: "Client Retention & Operational Reporting",
          date: "June 22, 2026",
          description: "Implemented mandatory monthly client meetings and standardized dashboard terminology for LSA reporting."
        },
        {
          title: "Software Infrastructure & Review Protocols",
          date: "June 12, 2026",
          description: "Transitioned to 24/7 remote computing for AI agents and optimized review request protocols via SMS."
        },
        {
          title: "Platform Prioritization & Tracking",
          date: "June 10, 2026",
          description: "Shifted focus to SMS for review requests and improved tracking clarity for sent vs. pending messages."
        },
        {
          title: "Technical Updates & Pricing Models",
          date: "June 8, 2026",
          description: "Refined radius tool errors and confirmed pricing shift to $200 per location."
        },
        {
          title: "Unification & Pricing Strategy",
          date: "June 5, 2026",
          description: "Unified service offerings and established a new universal pricing structure of $200 per location."
        },
        {
          title: "Marketing Strategy & Automation",
          date: "June 3, 2026",
          description: "Launched new marketing partnership and implemented AI-driven health monitoring."
        },
        {
          title: "AI System Transition",
          date: "May 29, 2026",
          description: "Transitioned to proprietary AI-managed phone numbers and prioritized onboarding to the Hermes AI agent."
        }
      ]
    };
  }
};
</script>

<style scoped>
/* Main Container */
.updates-container {
  /*max-width: 1100px;*/
  margin: 0 auto;
  padding: 32px 20px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, Helvetica, Arial, sans-serif;
  color: #0f172a;
  background-color: #f8fafc;
  min-height: 100vh;
}

/* Header */
.page-header {
  margin-bottom: 28px;
  padding: 20px;
}
.category-badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #3b82f6;
  background: #eff6ff;
  padding: 4px 10px;
  border-radius: 20px;
  margin-bottom: 8px;
}
.page-header h1 {
  font-size: 28px;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.02em;
}
.subtitle {
  color: #FFFFFF;
  margin: 6px 0 0 0;
  font-size: 15px;
}

/* Layout Grid */
.content-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

@media (min-width: 868px) {
  .content-grid {
    grid-template-columns: 360px 1fr;
    align-items: start;
  }
}

/* Card Styling */
.card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
}
.title-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
}
.title-with-icon .icon {
  font-size: 18px;
}
.card-header h2 {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  color: #0f172a;
}
.task-count {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 10px;
}

/* Task List */
.task-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.task-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  transition: all 0.2s ease;
}
.task-item:hover {
  border-color: #cbd5e1;
  background: #ffffff;
}
.task-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  user-select: none;
}
.task-checkbox {
  margin-top: 3px;
  accent-color: #2563eb;
  width: 16px;
  height: 16px;
  cursor: pointer;
}
.task-text {
  font-size: 13px;
  line-height: 1.4;
  color: #334155;
  font-weight: 500;
}

/* Timeline */
.timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.timeline-item {
  display: flex;
  gap: 16px;
  position: relative;
}
.timeline-marker {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.timeline-node {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #cbd5e1;
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 2px #e2e8f0;
  margin-top: 4px;
  flex-shrink: 0;
  transition: background-color 0.2s ease;
}
.timeline-node.latest {
  background: #2563eb;
  box-shadow: 0 0 0 3px #dbeafe;
}
.timeline-line {
  width: 2px;
  background: #e2e8f0;
  flex-grow: 1;
  margin-top: 4px;
  margin-bottom: 4px;
}
.timeline-content {
  padding-bottom: 24px;
  flex-grow: 1;
}
.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
}
.update-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}
.update-date {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
}
.badge-latest {
  color: #2563eb;
  font-weight: 600;
}
.update-description {
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
  margin: 4px 0 0 0;
}
</style>